'use client';
import { JACKPOT_STATE } from '@/utils/constants/daily-jackpots';
import { getDailyJackpots } from '@/lib/daily-jackpots';
import { useStore } from '@/store/store';
import type { DailyJackpotPrimary } from '@/types/zones/slices/daily-jackpot';
import { getHourFromDate } from '@/utils/date/get-hour-from-date';
import { formatNumberDefault } from '@/utils/numbers/format-number';
import { useQuery } from '@tanstack/react-query';
import { useGlobalContext } from '@/store/contexts/global-context';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';

type JackpotDescriptionProps = {
    jackpotFromContent: DailyJackpotPrimary;
    feedsCode: string;
    isJustWonState: boolean;
    showDescription: boolean;
};

export const JackpotDescription = ({ jackpotFromContent, feedsCode, showDescription }: JackpotDescriptionProps) => {
    const debug = useStore((s) => s.debug);
    const { featureFlags } = useGlobalContext();

    const isDailyJackpotsEnabled = featureFlags[PRODUCT_THROTTLES.DAILY_JACKPOTS];
    const { data = [] } = useQuery({
        queryKey: ['dailyJackpots', debug.mockedState],
        queryFn: () =>
            getDailyJackpots({
                state: debug.mockedState,
                cycle: debug.cycle,
                callback: debug.incrementDailyJackpotsCycle
            }),
        enabled: Boolean(feedsCode && isDailyJackpotsEnabled),
        refetchInterval: 30_000
    });
    const jackpot = data?.find((dailyJackpot) => `${dailyJackpot?.id}` === feedsCode);

    if (!jackpot) {
        return <></>;
    }

    const isJustWonState = jackpot?.state === JACKPOT_STATE.JUST_WON;
    const prevWinAmount = jackpot?.prevWinAmount || 0;

    const description = jackpotFromContent?.description?.[0]?.text || '';
    const primaryText = jackpotFromContent?.primary_text?.[0]?.text || '';
    const secondaryText = jackpotFromContent?.secondary_text?.[0]?.text || '';
    const primaryJustWonText = jackpotFromContent?.primary_just_won?.[0]?.text || '';
    const secondaryJustWonText = jackpotFromContent?.secondary_just_won?.[0]?.text || '';

    const hiddenDateText = getHourFromDate(jackpot?.time?.hidden as string);
    const mustBeWonBeforeText = getHourFromDate(jackpot?.time?.target as string);

    return (
        <div className="jackpot-details__content">
            {showDescription && <p className="description">{description}</p>}
            <p className="details details__primary">
                {isJustWonState ? primaryJustWonText : primaryText}
                <b> {isJustWonState ? prevWinAmount && formatNumberDefault(prevWinAmount) : mustBeWonBeforeText}</b>
            </p>
            <p className="details details__secondary">
                {isJustWonState ? secondaryJustWonText : secondaryText}
                <b> {isJustWonState ? hiddenDateText : prevWinAmount && formatNumberDefault(prevWinAmount)}</b>
            </p>
        </div>
    );
};
